import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Instagram, Linkedin } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { waitlistApi } from '../services/api';
import surveyBackground from '../assets/survey-background.png';
import { useAnalytics } from '../hooks/useAnalytics';
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel } from './ui/alert-dialog';

// Import survey components
import DemographicsStep from './survey/steps/DemographicsStep';
import EventTypesStep from './survey/steps/EventTypesStep';
import FrequencyStep from './survey/steps/FrequencyStep';
import PainPointsStep from './survey/steps/PainPointsStep';
import SurveyErrorBoundary from './survey/SurveyErrorBoundary';

// Constants
const AUTO_SAVE_DELAY = 2000; // 2 seconds
const RATE_LIMIT_RETRY_DELAY = 5000; // 5 seconds for rate limit errors
const DEFAULT_RETRY_DELAY = 1000; // 1 second for other errors
const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429
};

export function SurveyModal({ open, onOpenChange, email, profileToken, returnFocusRef }) {
  // Analytics
  const analytics = useAnalytics();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [demographicsQuestion, setDemographicsQuestion] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [skippedSteps, setSkippedSteps] = useState([]);

  // Form data for each step
  const [stepsData, setStepsData] = useState({
    1: { age: '', location: '', locationData: null, device: '', emailProvider: undefined },
    2: { eventTypes: [] },
    3: { frequency: '' },
    4: { painPoints: [] }
  });

  // Save state management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const pendingSaveRef = useRef(null);

  // Success state
  const [showSuccess, setShowSuccess] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null, // 'close' or 'skip'
    hasUnsavedData: false
  });

  // Track last email to detect changes
  const [lastEmail, setLastEmail] = useState(email);

  // Track if survey has been started for analytics
  const [surveyStarted, setSurveyStarted] = useState(false);

  // Track step view timing
  const stepViewTimeRef = useRef(null);

  const shellRef = useRef(null);
  const bodyRef = useRef(null);
  const questionsRef = useRef(null);
  const closeButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const [viewportStyle, setViewportStyle] = useState({});

  // Mobile keyboards resize the visual viewport without always changing 100dvh.
  useEffect(() => {
    if (!open || !window.visualViewport) return;
    const viewport = window.visualViewport;
    const updateViewport = () => {
      if (viewport.scale !== 1) {
        // CSS keeps following orientation changes while the browser handles zoom.
        setViewportStyle({});
        return;
      }
      setViewportStyle({
        '--survey-viewport-height': `${viewport.height}px`,
        '--survey-viewport-top': `${viewport.offsetTop}px`
      });
    };
    updateViewport();
    viewport.addEventListener('resize', updateViewport);
    viewport.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    return () => {
      viewport.removeEventListener('resize', updateViewport);
      viewport.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
    };
  }, [open]);

  const focusStepHeading = useCallback(() => {
    const heading = bodyRef.current?.querySelector('h2, h3');
    heading?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!open) return;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    if (questionsRef.current) questionsRef.current.scrollTop = 0;
    focusStepHeading();
  }, [open, currentStep, demographicsQuestion, showSuccess, focusStepHeading]);

  const totalSteps = 4;
  // The four demographics screens still save together as API step 1.
  const totalScreens = 7;
  const currentScreen = currentStep === 1 ? demographicsQuestion + 1 : currentStep + 3;

  const stepNames = {
    1: 'demographics',
    2: 'event_types',
    3: 'frequency',
    4: 'pain_points'
  };

  // Reset survey when email changes
  useEffect(() => {
    if (email && email !== lastEmail) {
      // Reset all survey state for new user
      setCurrentStep(1);
      setDemographicsQuestion(0);
      setCompletedSteps([]);
      setSkippedSteps([]);
      setStepsData({
        1: { age: '', location: '', locationData: null, device: '', emailProvider: undefined },
        2: { eventTypes: [] },
        3: { frequency: '' },
        4: { painPoints: [] }
      });
      setShowSuccess(false);
      setSurveyStarted(false);
      setLastEmail(email);
    }
  }, [email, lastEmail]);

  // Track survey start and step views
  useEffect(() => {
    if (open && email && !surveyStarted) {
      // Track survey start
      analytics.trackSurveyStart('waitlist_signup', email);
      setSurveyStarted(true);

      // Track first step view
      analytics.trackSurveyStepView(1, stepNames[1]);
      stepViewTimeRef.current = Date.now();
    }
  }, [open, email, surveyStarted, analytics]);

  // Track step changes
  useEffect(() => {
    if (open && surveyStarted && stepViewTimeRef.current) {
      // Track new step view
      analytics.trackSurveyStepView(currentStep, stepNames[currentStep]);

      // Update timing reference
      stepViewTimeRef.current = Date.now();
    }
  }, [currentStep, open, surveyStarted, analytics]);

  // Auto-save on data change with debouncing
  useEffect(() => {
    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Only set up auto-save if we have data and aren't already saving
    if (hasStepData(currentStep) && !isSubmitting && !pendingSaveRef.current) {
      autoSaveTimerRef.current = setTimeout(() => {
        // Check again when timer fires to avoid race conditions
        if (isMountedRef.current && !isSubmitting && !pendingSaveRef.current) {
          saveCurrentStep(false, false);
        }
      }, AUTO_SAVE_DELAY);
    }

    // Cleanup function
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [stepsData[currentStep], currentStep]); // Only depend on current step data

  // Save current step data with race condition prevention
  const saveCurrentStep = async (completed = false, skipped = false) => {
    if (!email) return;
    // Invalid ages must not be sent by autosave or when leaving the survey.
    const age = Number(stepsData[1].age);
    if (currentStep === 1 && stepsData[1].age !== '' && (!Number.isInteger(age) || age < 18 || age > 120)) return false;

    // Prevent concurrent saves
    if (pendingSaveRef.current) {
      console.log('Save already in progress, skipping duplicate request');
      return false;
    }

    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    pendingSaveRef.current = true;

    try {
      await waitlistApi.updateStep(
        email,
        currentStep,
        stepsData[currentStep],
        completed,
        skipped,
        profileToken
      );

      // Update completed/skipped steps only if component is still mounted
      if (isMountedRef.current) {
        if (completed && !completedSteps.includes(currentStep)) {
          setCompletedSteps(prev => [...prev, currentStep]);
        }
        if (skipped && !skippedSteps.includes(currentStep)) {
          setSkippedSteps(prev => [...prev, currentStep]);
        }
      }

      pendingSaveRef.current = false;
      return true;
    } catch (error) {
      console.error('Failed to save step:', error);
      pendingSaveRef.current = false;

      // Implement exponential backoff for retries
      const retryDelay = error.status === HTTP_STATUS.TOO_MANY_REQUESTS ? RATE_LIMIT_RETRY_DELAY : DEFAULT_RETRY_DELAY;

      // Retry with backoff only if component is still mounted.
      // Do not retry on client errors that won't change on retry (400/401/422).
      if (
        isMountedRef.current &&
        error.status !== HTTP_STATUS.BAD_REQUEST &&
        error.status !== HTTP_STATUS.UNAUTHORIZED &&
        error.status !== HTTP_STATUS.FORBIDDEN &&
        error.status !== HTTP_STATUS.UNPROCESSABLE_ENTITY
      ) {
        retryTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            saveCurrentStep(completed, skipped);
          }
        }, retryDelay);
      }

      return false;
    }
  };

  // Check if step has any data
  const hasStepData = (step) => {
    const data = stepsData[step];
    switch(step) {
      case 1:
        return data.age || data.location || data.device || data.emailProvider;
      case 2:
        return data.eventTypes.length > 0;
      case 3:
        return !!data.frequency;
      case 4:
        return data.painPoints.length > 0;
      default:
        return false;
    }
  };

  // Check if step is valid for proceeding
  const isStepValid = (step) => {
    const data = stepsData[step];
    switch(step) {
      case 1:
        if (!Number.isInteger(Number(data.age)) || Number(data.age) < 18 || Number(data.age) > 120) return false;
        if (demographicsQuestion === 0) return true;
        if (!data.location.trim()) return false;
        if (demographicsQuestion === 1) return true;
        if (!data.device) return false;
        return demographicsQuestion === 2 || ['gmail', 'outlook', 'icloud', 'yahoo', 'proton', 'other'].includes(data.emailProvider);
      case 2:
        return data.eventTypes.length > 0;
      case 3:
        return !!data.frequency;
      case 4:
        return data.painPoints.length > 0;
      default:
        return false;
    }
  };

  // Handle step data change
  const handleStepDataChange = (newData) => {
    setStepsData({
      ...stepsData,
      [currentStep]: newData
    });
  };

  // Get answer category for analytics
  const getAnswerCategory = (step, data) => {
    switch(step) {
      case 1:
        return `age:${data.age ? Math.floor(data.age / 10) * 10 + 's' : 'unknown'},location:${data.location || 'unknown'},device:${data.device || 'unknown'}`;
      case 2:
        return data.eventTypes.length > 0 ? data.eventTypes.join(',').slice(0, 50) : 'none';
      case 3:
        return data.frequency || 'unknown';
      case 4:
        return data.painPoints.length > 0 ? `${data.painPoints.length}_selected` : 'none';
      default:
        return 'unknown';
    }
  };

  // Handle next button
  const handleNext = async () => {
    if (!isStepValid(currentStep)) return;
    if (currentStep === 1 && demographicsQuestion < 3) {
      setDemographicsQuestion(question => question + 1);
      return;
    }
    setIsSubmitting(true);

    const saved = await saveCurrentStep(true, false);

    if (saved) {
      // Track step completion
      const answerCategory = getAnswerCategory(currentStep, stepsData[currentStep]);
      analytics.trackSurveyStepComplete(currentStep, stepNames[currentStep], answerCategory);

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final step completed - track survey completion
        const totalCompleted = completedSteps.length + 1; // +1 for current step
        const totalSkipped = skippedSteps.length;
        analytics.trackSurveyComplete(totalCompleted, totalSkipped);

        setShowSuccess(true);
      }
    }

    setIsSubmitting(false);
  };

  // Handle skip button
  const handleSkip = async () => {
    setIsSubmitting(true);

    const saved = await saveCurrentStep(false, true);

    if (saved) {
      // Track step skip
      analytics.trackSurveyStepSkip(currentStep, stepNames[currentStep]);

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final step skipped - track survey completion (even if partially)
        const totalCompleted = completedSteps.length;
        const totalSkipped = skippedSteps.length + 1; // +1 for current step
        analytics.trackSurveyComplete(totalCompleted, totalSkipped);

        onOpenChange(false);
      }
    }

    setIsSubmitting(false);
  };

  // Handle back button
  const handleBack = () => {
    if (currentStep === 1 && demographicsQuestion > 0) {
      setDemographicsQuestion(question => question - 1);
      return;
    }
    if (currentStep > 1) {
      if (currentStep === 2) setDemographicsQuestion(3);
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle modal close
  const handleClose = () => {
    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      action: 'close',
      hasUnsavedData: hasStepData(currentStep)
    });
  };

  // Handle confirmed close from dialog
  const handleConfirmedClose = async () => {
    // Save current step before closing if there's data
    if (hasStepData(currentStep)) {
      await saveCurrentStep(false, false);
    }

    // Track survey abandon if not completed
    if (!showSuccess && surveyStarted) {
      const totalCompleted = completedSteps.length;
      analytics.trackSurveyAbandon(currentStep, totalCompleted);
    }

    setConfirmDialog({ open: false, action: null, hasUnsavedData: false });
    onOpenChange(false);
  };

  // Handle skip button on first step - closes immediately but tracks analytics
  const handleSkipClose = async () => {
    // Save current step if there's data
    if (hasStepData(currentStep)) {
      await saveCurrentStep(false, false);
    }

    // Track survey abandon
    if (surveyStarted) {
      const totalCompleted = completedSteps.length;
      analytics.trackSurveyAbandon(currentStep, totalCompleted);
    }

    onOpenChange(false);
  };

  // Render current step component
  const renderStep = () => {
    const props = {
      data: stepsData[currentStep],
      onChange: handleStepDataChange,
      disabled: isSubmitting
    };

    switch(currentStep) {
      case 1:
        return <DemographicsStep key={demographicsQuestion} {...props} question={demographicsQuestion} onNext={handleNext} onLocationSelected={() => nextButtonRef.current?.focus({ preventScroll: true })} />;
      case 2:
        return <EventTypesStep {...props} />;
      case 3:
        return <FrequencyStep {...props} />;
      case 4:
        return <PainPointsStep {...props} />;
      default:
        return null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Clear all timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }

      // Reset pending save flag
      pendingSaveRef.current = false;
    };
  }, []);

  // Handle close on success screen (no confirmation needed)
  const handleSuccessClose = () => {
    onOpenChange(false);
  };

  const requestClose = () => {
    if (showSuccess) handleSuccessClose();
    else handleClose();
  };
  const isIntroStep = currentStep === 1 && !showSuccess;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) requestClose(); }}>
      <DialogContent
        ref={shellRef}
        showCloseButton={false}
        className="survey-shell"
        style={{ ...viewportStyle, backgroundImage: `url(${surveyBackground})` }}
        onOpenAutoFocus={(event) => { event.preventDefault(); focusStepHeading(); }}
        onCloseAutoFocus={returnFocusRef ? (event) => {
          event.preventDefault();
          returnFocusRef.current?.focus({ preventScroll: true });
        } : undefined}
        onInteractOutside={(event) => {
          event.preventDefault();
          if (!confirmDialog.open) requestClose();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          if (!confirmDialog.open) requestClose();
        }}
      >
        <DialogTitle className="sr-only">Beta signup survey</DialogTitle>
        <DialogDescription className="sr-only">
          Share a few details to help shape Antix. You can leave the survey at any time.
        </DialogDescription>
        <div className={`survey-frame${isIntroStep ? ' survey-frame--intro' : ''}`} style={viewportStyle} role="region" aria-label="Survey panel">
        <SurveyErrorBoundary onClose={() => onOpenChange(false)}>
          <header className="survey-header">
            <div className="survey-progress" aria-label={showSuccess ? 'Survey complete' : `Step ${currentScreen} of ${totalScreens}`}>
              <div className="flex gap-2" aria-hidden="true">
                {Array.from({ length: totalScreens }, (_, i) => (
                  <span key={i} className={`h-2 w-2 rounded-full ${showSuccess || i < currentScreen ? 'bg-white' : 'bg-white/30'}`} />
                ))}
              </div>
              <span className="text-sm text-white/70">{showSuccess ? 'Complete' : `Step ${currentScreen} of ${totalScreens}`}</span>
            </div>
            <Button ref={closeButtonRef} variant="ghost" size="icon" onClick={requestClose} className="survey-close" aria-label="Close">
              <X aria-hidden="true" />
            </Button>
          </header>

          <div ref={bodyRef} className={`survey-body${isIntroStep ? ' survey-body--intro' : ''}`} role="region" aria-label="Survey questions">
            <div ref={questionsRef} className="survey-questions">
            {showSuccess ? (
              <div className="survey-success">
                <h2 tabIndex={-1} className="survey-heading text-3xl mb-4">Thank you for sharing</h2>
                <p className="text-white/80 text-lg mb-8">These insights are helping us build the best app for music lovers</p>
                <p className="text-white/70 mb-4">Follow us and follow our progress.</p>
                <div className="flex items-center justify-center space-x-3">
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/antix.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20 border border-white/20"
                    aria-label="Instagram"
                  >
                    <Instagram size={24} className="text-white" />
                  </a>

                  {/* TikTok */}
                  <a
                    href="https://www.tiktok.com/@antix.the.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20 border border-white/20"
                    aria-label="TikTok"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="text-white"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </a>

                  {/* LinkedIn */}
                  <a
                    href="https://www.linkedin.com/company/antixapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20 border border-white/20"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={24} className="text-white" />
                  </a>
                </div>
              </div>
            ) : (
              <>
                {currentStep === 1 && demographicsQuestion === 0 && (
                  <div className="survey-intro">
                    <h2 tabIndex={-1} className="survey-heading text-[28px] sm:text-[36px] mb-2">You're on the list! 🎉</h2>
                    <p className="text-white/70">We'd love to get to know you better, if you have 60 seconds.</p>
                  </div>
                )}
                {renderStep()}
              </>
            )}
            </div>

          <footer className="survey-footer">
            {showSuccess ? (
              <Button onClick={handleSuccessClose} className="survey-next ml-auto">Close</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={currentScreen > 1 ? handleBack : handleSkipClose} disabled={isSubmitting} className="survey-back">
                  {currentScreen > 1 ? <><ChevronLeft aria-hidden="true" />Back</> : 'Skip'}
                </Button>
                <Button ref={nextButtonRef} onClick={handleNext} disabled={isSubmitting || !isStepValid(currentStep)} className="survey-next">
                  {isSubmitting ? 'Saving...' : currentStep === totalSteps ? 'Complete' : <>Next<ChevronRight aria-hidden="true" /></>}
                </Button>
              </>
            )}
          </footer>
          </div>
        </SurveyErrorBoundary>
        </div>

        <AlertDialog open={confirmDialog.open} onOpenChange={(nextOpen) => {
          if (!nextOpen) setConfirmDialog({ open: false, action: null, hasUnsavedData: false });
        }}>
          <AlertDialogContent
            className="survey-exit"
            style={viewportStyle}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (open) closeButtonRef.current?.focus({ preventScroll: true });
            }}
          >
            <AlertDialogTitle>Before you go!</AlertDialogTitle>
            <AlertDialogDescription>Don't forget to go to your email and confirm your subscription.</AlertDialogDescription>
            <div className="flex flex-wrap justify-end gap-3 mt-2">
              <AlertDialogCancel className="survey-back m-0">Stay</AlertDialogCancel>
              <Button onClick={handleConfirmedClose} className="survey-next">Got it!</Button>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
